/**
 * WHAT: PostgreSQL user access report with server-side pagination and superuser/role-type filtering.
 * WHY:  PostgreSQL environments can have large user populations with role inheritance; this report
 *       lets compliance teams review privilege levels, superuser status, and user types to support
 *       attestation workflows and least-privilege audits.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import API_CONFIG from '../config/api.config';
import { WideModal, PostgresqlIcon } from './common';
import './OracleUserAccessReport.css'; // Reuse Oracle styles

/**
 * WHAT: Renders a paginated PostgreSQL user access report with filters and privilege detail modals.
 * WHY:  Enables PostgreSQL DBAs and auditors to examine per-user access across databases,
 *       filter by superuser status and risk level, and drill into privilege details for audit evidence.
 */
const PostgresqlUserAccessReport = () => {
  // State management
  const [databases, setDatabases] = useState([]);
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showPrivilegeModal, setShowPrivilegeModal] = useState(false);
  const [privilegeModalData, setPrivilegeModalData] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [collectionDates, setCollectionDates] = useState([]);

  // Filters
  const [filters, setFilters] = useState({
    database_id: '',
    collection_date: '',
    risk_level: '',
    is_superuser: '',
    username: '',
    user_type: '',
    privilege_level: ''
  });

  // Active privilege tiers for the matrix-driven dropdown. Disabling a tier
  // for PostgreSQL in the privilege matrix removes it from this list.
  const [activeTiers, setActiveTiers] = useState([]);

  // Load databases + active tiers
  useEffect(() => {
    const fetchDatabases = async () => {
      try {
        const response = await axios.get(
          API_CONFIG.getDatabasesEndpoint('postgresql-access/databases/')
        );
        if (response.data.success) {
          setDatabases(response.data.databases);
        }
      } catch (err) {
        console.error('Error fetching databases:', err);
      }
    };
    const fetchActiveTiers = async () => {
      try {
        const response = await axios.get(
          API_CONFIG.getDatabasesEndpoint('privilege-matrix/postgresql/active-tiers/')
        );
        if (response.data?.success) {
          setActiveTiers(response.data.tiers || []);
        }
      } catch (err) {
        console.error('Failed to fetch active tiers for PostgreSQL:', err);
      }
    };
    fetchDatabases();
    fetchActiveTiers();
  }, []);

  const fetchCollectionDates = async (databaseId = '') => {
    try {
      const params = databaseId ? `?database_id=${databaseId}` : '';
      const response = await axios.get(
        API_CONFIG.getDatabasesEndpoint(`postgresql-access/collection-dates/${params}`)
      );
      setCollectionDates(response.data.collection_dates || []);
    } catch (err) {
      console.error('Error fetching collection dates:', err);
      setCollectionDates([]);
    }
  };

  // Load summary
  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const params = filters.database_id ? `?database_id=${filters.database_id}` : '';
        const response = await axios.get(
          API_CONFIG.getDatabasesEndpoint(`postgresql-access/summary/${params}`)
        );
        if (response.data.success) {
          setSummary(response.data.summary);
        }
      } catch (err) {
        console.error('Error fetching summary:', err);
      }
    };
    fetchSummary();
  }, [filters.database_id]);

  // Load users
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const requestData = {
        ...filters,
        page: currentPage,
        page_size: pageSize,
        is_superuser: filters.is_superuser === '' ? null : filters.is_superuser === 'true'
      };

      const response = await axios.post(
        API_CONFIG.getDatabasesEndpoint('postgresql-access/report/'),
        requestData
      );

      if (response.data.success) {
        setUsers(response.data.users);
        setTotalCount(response.data.total_count);
        setTotalPages(response.data.total_pages);
      } else {
        // Not success — clear so we don't keep the previous DB's rows.
        setUsers([]);
        setTotalCount(0);
        setTotalPages(0);
        setError(response.data.error || 'No data for this database.');
      }
    } catch (err) {
      // Clear the report so a failed load (e.g. an uncollected/removed DB) shows
      // an empty report — NOT the previously-selected database's stale rows.
      setUsers([]);
      setTotalCount(0);
      setTotalPages(0);
      setError(err.response?.data?.error || 'Failed to fetch user access report');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, pageSize]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    // When database changes, re-fetch collection dates and reset collection_date
    if (field === 'database_id') {
      newFilters.collection_date = '';
      fetchCollectionDates(value);
    }
    setFilters(newFilters);
    setCurrentPage(1);
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      database_id: '',
      collection_date: '',
      risk_level: '',
      is_superuser: '',
      username: '',
      user_type: '',
      privilege_level: ''
    });
    setCollectionDates([]);
    setCurrentPage(1);
  };

  // View user details
  const handleViewUser = async (username, databaseId) => {
    try {
      const params = databaseId ? `?database_id=${databaseId}` : '';
      const response = await axios.get(
        API_CONFIG.getDatabasesEndpoint(`postgresql-access/user/${username}/${params}`)
      );
      if (response.data.success) {
        setSelectedUser(response.data.user);
        setShowUserModal(true);
      }
    } catch (err) {
      console.error('Error fetching user details:', err);
    }
  };

  // Show privilege modal
  const handleShowPrivileges = (user, privilegeType) => {
    setPrivilegeModalData({
      username: user.username,
      database: user.database_name,
      type: privilegeType,
      data: getPrivilegeData(user, privilegeType)
    });
    setShowPrivilegeModal(true);
  };

  // Parse JSONB field - handles both string and array formats
  const parseJsonField = (field) => {
    if (!field) return [];
    if (Array.isArray(field)) return field;
    if (typeof field === 'string') {
      try {
        const parsed = JSON.parse(field);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  // Get privilege data based on type
  const getPrivilegeData = (user, type) => {
    switch (type) {
      case 'roles':
        return parseJsonField(user.memberships);
      case 'table_privs':
        return parseJsonField(user.table_privileges);
      case 'schema_privs':
        return parseJsonField(user.schema_privileges);
      case 'db_privs':
        return parseJsonField(user.database_privileges);
      case 'function_privs':
        return parseJsonField(user.function_privileges);
      default:
        return [];
    }
  };

  // Export to CSV
  const handleExportCSV = async () => {
    try {
      const response = await axios.post(
        API_CONFIG.getDatabasesEndpoint('postgresql-access/export/csv/'),
        filters,
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `postgresql_user_access_report_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error exporting CSV:', err);
    }
  };

  // Risk level text indicator with dot
  const getRiskText = (level) => {
    const styles = {
      'CRITICAL': 'text-danger',
      'HIGH': 'text-warning',
      'MEDIUM': 'text-info',
      'LOW': 'text-secondary'
    };
    return (
      <span className={styles[level] || 'text-secondary'}>
        <i className="fas fa-circle fa-xs me-1"></i>{level}
      </span>
    );
  };

  // Privilege level text indicator
  const getPrivilegeLevelText = (level, levelName) => {
    const styles = {
      6: 'text-danger fw-bold',
      5: 'text-danger',
      4: 'text-warning',
      3: 'text-warning',
      2: 'text-info',
      1: 'text-info',
      0: 'text-secondary',
      '-1': 'text-muted'
    };
    return (
      <span className={styles[level] || 'text-secondary'}>
        {levelName || 'UNKNOWN'}
      </span>
    );
  };

  // Pagination
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <nav>
        <ul className="pagination pagination-sm mb-0">
          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => setCurrentPage(1)}>&laquo;</button>
          </li>
          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => setCurrentPage(prev => prev - 1)}>&lsaquo;</button>
          </li>
          {pages.map(page => (
            <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
              <button className="page-link" onClick={() => setCurrentPage(page)}>{page}</button>
            </li>
          ))}
          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => setCurrentPage(prev => prev + 1)}>&rsaquo;</button>
          </li>
          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => setCurrentPage(totalPages)}>&raquo;</button>
          </li>
        </ul>
      </nav>
    );
  };

  return (
    <div className="oracle-user-access-report">
      {/* Header */}
      <div className="report-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h2 className="mb-1">
              <PostgresqlIcon size={28} className="me-2" />
              PostgreSQL User Access Report
            </h2>
            <p className="text-muted mb-0">Compliance Dashboard - User Privilege Analysis</p>
          </div>
          <div>
            <button className="btn btn-outline-primary me-2" onClick={fetchUsers}>
              <i className="fas fa-sync-alt me-1"></i> Refresh
            </button>
            <button className="btn btn-success" onClick={handleExportCSV}>
              <i className="fas fa-file-csv me-1"></i> Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="row mb-4">
          <div className="col-md-2">
            <div className="card bg-primary text-white">
              <div className="card-body text-center py-3">
                <h3 className="mb-0">{summary.total_users || 0}</h3>
                <small>Total Users/Roles</small>
              </div>
            </div>
          </div>
          <div className="col-md-2">
            <div className="card bg-info text-white">
              <div className="card-body text-center py-3">
                <h3 className="mb-0">{summary.login_users || 0}</h3>
                <small>Login Users</small>
              </div>
            </div>
          </div>
          <div className="col-md-2">
            <div className="card bg-danger text-white">
              <div className="card-body text-center py-3">
                <h3 className="mb-0">{summary.superusers || 0}</h3>
                <small>Superusers</small>
              </div>
            </div>
          </div>
          <div className="col-md-2">
            <div className="card bg-warning text-dark">
              <div className="card-body text-center py-3">
                <h3 className="mb-0">{summary.users_with_critical || 0}</h3>
                <small>Critical Privs</small>
              </div>
            </div>
          </div>
          <div className="col-md-2">
            <div className="card bg-secondary text-white">
              <div className="card-body text-center py-3">
                <h3 className="mb-0">{summary.roles_only || 0}</h3>
                <small>Roles Only</small>
              </div>
            </div>
          </div>
          <div className="col-md-2">
            <div className="card bg-dark text-white">
              <div className="card-body text-center py-3">
                <h3 className="mb-0">{summary.database_count || 0}</h3>
                <small>Databases</small>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Risk Distribution */}
      {summary?.risk_distribution && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header bg-secondary text-white py-2">
                <h6 className="mb-0"><i className="fas fa-chart-pie me-2"></i>Risk Distribution</h6>
              </div>
              <div className="card-body py-2">
                <div className="d-flex justify-content-around">
                  <div className="text-center">
                    <span className="text-danger fs-4 fw-bold">{summary.risk_distribution.CRITICAL || 0}</span>
                    <div className="small text-danger"><i className="fas fa-circle fa-xs me-1"></i>Critical</div>
                  </div>
                  <div className="text-center">
                    <span className="text-warning fs-4 fw-bold">{summary.risk_distribution.HIGH || 0}</span>
                    <div className="small text-warning"><i className="fas fa-circle fa-xs me-1"></i>High</div>
                  </div>
                  <div className="text-center">
                    <span className="text-info fs-4 fw-bold">{summary.risk_distribution.MEDIUM || 0}</span>
                    <div className="small text-info"><i className="fas fa-circle fa-xs me-1"></i>Medium</div>
                  </div>
                  <div className="text-center">
                    <span className="text-secondary fs-4 fw-bold">{summary.risk_distribution.LOW || 0}</span>
                    <div className="small text-secondary"><i className="fas fa-circle fa-xs me-1"></i>Low</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-header bg-light py-2">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0"><i className="fas fa-filter me-2"></i>Filters</h6>
            <button className="btn btn-sm btn-outline-secondary" onClick={clearFilters}>
              Clear All
            </button>
          </div>
        </div>
        <div className="card-body py-2">
          <div className="row g-2">
            <div className="col-md-2">
              <select
                className="form-select form-select-sm"
                value={filters.database_id}
                onChange={(e) => handleFilterChange('database_id', e.target.value)}
              >
                <option value="">All Databases</option>
                {databases.map(db => (
                  <option key={db.id} value={db.id}>{db.database_name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-select form-select-sm"
                value={filters.collection_date}
                onChange={(e) => handleFilterChange('collection_date', e.target.value)}
              >
                <option value="">Latest</option>
                {collectionDates.map((cd, idx) => (
                  <option key={idx} value={cd.value}>{cd.label}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-select form-select-sm"
                value={filters.risk_level}
                onChange={(e) => handleFilterChange('risk_level', e.target.value)}
              >
                <option value="">All Risk Levels</option>
                <option value="CRITICAL">Critical</option>
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-select form-select-sm"
                value={filters.privilege_level}
                onChange={(e) => handleFilterChange('privilege_level', e.target.value)}
              >
                <option value="">All Privilege Levels</option>
                {activeTiers.map(t => (
                  <option key={t.tier_name} value={t.tier_value}>{t.tier_name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-select form-select-sm"
                value={filters.is_superuser}
                onChange={(e) => handleFilterChange('is_superuser', e.target.value)}
              >
                <option value="">All Users</option>
                <option value="true">Superusers Only</option>
                <option value="false">Non-Superusers</option>
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-select form-select-sm"
                value={filters.user_type}
                onChange={(e) => handleFilterChange('user_type', e.target.value)}
              >
                <option value="">All Types</option>
                <option value="USER">Login Users</option>
                <option value="ROLE">Roles Only</option>
              </select>
            </div>
            <div className="col-md-4">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search username..."
                value={filters.username}
                onChange={(e) => handleFilterChange('username', e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert-danger mb-4">
          <i className="fas fa-exclamation-circle me-2"></i>{error}
        </div>
      )}

      {/* Results Table */}
      <div className="card">
        <div className="card-header bg-secondary text-white py-2">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">
              <i className="fas fa-users me-2"></i>
              User Access List
              {totalCount > 0
                ? ` (Showing ${((currentPage - 1) * pageSize) + 1}-${Math.min(currentPage * pageSize, totalCount)} of ${totalCount} users)`
                : ` (0 users)`}
            </h6>
            <div className="d-flex align-items-center gap-3">
              <select
                className="form-select form-select-sm"
                style={{ width: '100px' }}
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              {renderPagination()}
            </div>
          </div>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover table-sm mb-0">
                <thead className="table-dark">
                  <tr>
                    <th>Database</th>
                    <th>Username</th>
                    <th>Account Type</th>
                    <th>Type</th>
                    <th className="text-center">Superuser</th>
                    <th className="text-center">Login</th>
                    <th className="text-center">CreateDB</th>
                    <th className="text-center">CreateRole</th>
                    <th className="text-center">Roles</th>
                    <th className="text-center">Table Privs</th>
                    <th className="text-center">Schema Privs</th>
                    <th>Privilege Level</th>
                    <th>Risk Level</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan="14" className="text-center py-4 text-muted">
                        No users found matching the criteria
                      </td>
                    </tr>
                  ) : (
                    users.map((user, idx) => (
                      <tr key={`${user.database_id}-${user.username}-${idx}`}>
                        <td>
                          <span className="text-muted">{user.database_name}</span>
                        </td>
                        <td>
                          <strong>{user.username}</strong>
                          {user.username.startsWith('svc_') && (
                            <span className="text-info ms-1" title="Service Account"><i className="fas fa-robot fa-xs"></i></span>
                          )}
                        </td>
                        <td>{user.account_type || '—'}</td>
                        <td>
                          <span className={user.role_type === 'USER' ? 'text-primary' : 'text-secondary'}>
                            {user.role_type}
                          </span>
                        </td>
                        <td className="text-center">
                          {user.is_superuser ? (
                            <i className="fas fa-crown text-danger" title="Superuser"></i>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td className="text-center">
                          {user.can_login ? (
                            <i className="fas fa-check text-success"></i>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td className="text-center">
                          {user.can_create_db ? (
                            <i className="fas fa-check text-warning"></i>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td className="text-center">
                          {user.can_create_role ? (
                            <i className="fas fa-check text-warning"></i>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td className="text-center">
                          {user.role_count > 0 ? (
                            <button
                              className="btn btn-link btn-sm p-0"
                              onClick={() => handleShowPrivileges(user, 'roles')}
                            >
                              {user.role_count}
                            </button>
                          ) : (
                            <span className="text-muted">0</span>
                          )}
                        </td>
                        <td className="text-center">
                          {user.table_priv_count > 0 ? (
                            <button
                              className="btn btn-link btn-sm p-0"
                              onClick={() => handleShowPrivileges(user, 'table_privs')}
                            >
                              {user.table_priv_count}
                            </button>
                          ) : (
                            <span className="text-muted">0</span>
                          )}
                        </td>
                        <td className="text-center">
                          {user.schema_priv_count > 0 ? (
                            <button
                              className="btn btn-link btn-sm p-0"
                              onClick={() => handleShowPrivileges(user, 'schema_privs')}
                            >
                              {user.schema_priv_count}
                            </button>
                          ) : (
                            <span className="text-muted">0</span>
                          )}
                        </td>
                        <td>{getPrivilegeLevelText(user.max_priv_level, user.max_priv_level_name || user.privilege_level_name)}</td>
                        <td>{getRiskText(user.risk_level)}</td>
                        <td>
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => handleViewUser(user.username, user.database_id)}
                            title="View Details"
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {!loading && users.length > 0 && (
          <div className="card-footer bg-light py-2">
            <div className="d-flex justify-content-between align-items-center">
              <small className="text-muted">
                Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalCount)} of {totalCount}
              </small>
              {renderPagination()}
            </div>
          </div>
        )}
      </div>

      {/* User Details Modal */}
      <WideModal
        show={showUserModal && selectedUser}
        onClose={() => setShowUserModal(false)}
        title={`User Details: ${selectedUser?.username || ''}`}
        headerIcon="fa-user"
        headerClass="bg-primary text-white"
        footer={
          <button type="button" className="btn btn-secondary" onClick={() => setShowUserModal(false)}>
            Close
          </button>
        }
      >
        {selectedUser && (
          <>
            <div className="row">
                  {/* User Info */}
                  <div className="col-md-6">
                    <div className="card mb-3">
                      <div className="card-header bg-light py-2">
                        <h6 className="mb-0"><i className="fas fa-info-circle me-2"></i>User Information</h6>
                      </div>
                      <div className="card-body">
                        <table className="table table-sm mb-0">
                          <tbody>
                            <tr><th width="40%">Database</th><td>{selectedUser.database_name}</td></tr>
                            <tr><th>Username</th><td>{selectedUser.username}</td></tr>
                            <tr><th>Type</th><td><span className={selectedUser.role_type === 'USER' ? 'text-primary' : 'text-secondary'}>{selectedUser.role_type}</span></td></tr>
                            <tr><th>Superuser</th><td>{selectedUser.is_superuser ? <span className="text-danger"><i className="fas fa-crown"></i> Yes</span> : 'No'}</td></tr>
                            <tr><th>Can Login</th><td>{selectedUser.can_login ? 'Yes' : 'No'}</td></tr>
                            <tr><th>Can Create DB</th><td>{selectedUser.can_create_db ? 'Yes' : 'No'}</td></tr>
                            <tr><th>Can Create Role</th><td>{selectedUser.can_create_role ? 'Yes' : 'No'}</td></tr>
                            <tr><th>Replication</th><td>{selectedUser.is_replication ? 'Yes' : 'No'}</td></tr>
                            <tr><th>Bypass RLS</th><td>{selectedUser.bypass_rls ? <span className="text-danger">Yes</span> : 'No'}</td></tr>
                            <tr><th>Connection Limit</th><td>{selectedUser.connection_limit === -1 ? 'Unlimited' : selectedUser.connection_limit}</td></tr>
                            {selectedUser.valid_until && (
                              <tr><th>Valid Until</th><td>{new Date(selectedUser.valid_until).toLocaleString()}</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* Privilege Stats */}
                  <div className="col-md-6">
                    <div className="card mb-3">
                      <div className="card-header bg-light py-2">
                        <h6 className="mb-0"><i className="fas fa-chart-bar me-2"></i>Privilege Statistics</h6>
                      </div>
                      <div className="card-body">
                        {(() => {
                          const stats = typeof selectedUser.stats === 'string'
                            ? JSON.parse(selectedUser.stats)
                            : selectedUser.stats;
                          return stats ? (
                            <table className="table table-sm mb-0">
                              <tbody>
                                <tr><th width="60%">Risk Level</th><td>{getRiskText(stats.risk_assessment?.risk_level || 'LOW')}</td></tr>
                                <tr><th>Risk Score</th><td>{stats.risk_assessment?.risk_score || 0}</td></tr>
                                <tr><th>Critical Privileges</th><td><span className={stats.critical_privilege_count > 0 ? 'text-danger fw-bold' : 'text-muted'}>{stats.critical_privilege_count || 0}</span></td></tr>
                                <tr><th>Total Direct Privileges</th><td>{stats.privilege_counts?.total_direct || 0}</td></tr>
                                <tr><th>Membership Count</th><td>{stats.membership_count || 0}</td></tr>
                                <tr><th>Tables with Select</th><td>{stats.tables_with_select || 0}</td></tr>
                                <tr><th>Tables with Modify</th><td>{stats.tables_with_modify || 0}</td></tr>
                              </tbody>
                            </table>
                          ) : (
                            <p className="text-muted mb-0">No statistics available</p>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Role Memberships */}
                {(() => {
                  const memberships = parseJsonField(selectedUser.memberships);
                  return memberships.length > 0 && (
                    <div className="card mb-3">
                      <div className="card-header bg-light py-2">
                        <h6 className="mb-0"><i className="fas fa-users me-2"></i>Role Memberships ({memberships.length})</h6>
                      </div>
                      <div className="card-body p-0">
                        <table className="table table-sm table-hover mb-0">
                          <thead className="table-secondary">
                            <tr>
                              <th>Granted Role</th>
                              <th>Admin Option</th>
                              <th>Grantor</th>
                            </tr>
                          </thead>
                          <tbody>
                            {memberships.map((role, idx) => (
                              <tr key={idx}>
                                <td>{role.granted_role || role.role_name || (typeof role === 'string' ? role : JSON.stringify(role))}</td>
                                <td>{role.admin_option ? <i className="fas fa-check text-success"></i> : '-'}</td>
                                <td>{role.grantor || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}

                {/* Table Privileges */}
                {(() => {
                  const tablePrivs = parseJsonField(selectedUser.table_privileges);
                  return tablePrivs.length > 0 && (
                    <div className="card mb-3">
                      <div className="card-header bg-light py-2">
                        <h6 className="mb-0"><i className="fas fa-table me-2"></i>Table Privileges ({tablePrivs.length})</h6>
                      </div>
                      <div className="card-body p-0" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        <table className="table table-sm table-hover mb-0">
                          <thead className="table-secondary sticky-top">
                            <tr>
                              <th>Schema</th>
                              <th>Table</th>
                              <th>Privileges</th>
                              <th>Grantable</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tablePrivs.slice(0, 50).map((priv, idx) => (
                              <tr key={idx}>
                                <td>{priv.schema_name || priv.table_schema}</td>
                                <td>{priv.table_name}</td>
                                <td>
                                  {Array.isArray(priv.privileges)
                                    ? priv.privileges.join(', ')
                                    : (priv.privilege_type || priv.privileges)}
                                </td>
                                <td>{priv.is_grantable ? <i className="fas fa-check text-success"></i> : '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {tablePrivs.length > 50 && (
                          <div className="text-center py-2 text-muted">
                            Showing first 50 of {tablePrivs.length} privileges
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
          </>
        )}
      </WideModal>

      {/* Privilege Detail Modal */}
      <WideModal
        show={showPrivilegeModal && privilegeModalData}
        onClose={() => setShowPrivilegeModal(false)}
        title={privilegeModalData ? `${privilegeModalData.type.replace('_', ' ').toUpperCase()} - ${privilegeModalData.username}` : ''}
        headerIcon="fa-list"
        headerClass="bg-info text-white"
        maxWidth="900px"
        footer={
          <button type="button" className="btn btn-secondary" onClick={() => setShowPrivilegeModal(false)}>
            Close
          </button>
        }
      >
        {privilegeModalData && (
          <>
            <p className="text-muted mb-2">Database: {privilegeModalData.database}</p>
            {privilegeModalData.data.length > 0 ? (
              <div className="table-responsive" style={{ maxHeight: '400px' }}>
                <table className="table table-sm table-hover mb-0">
                  <thead className="table-secondary sticky-top">
                    <tr>
                      {privilegeModalData.type === 'roles' ? (
                        <>
                          <th>Granted Role</th>
                          <th>Admin Option</th>
                        </>
                      ) : privilegeModalData.type === 'table_privs' ? (
                        <>
                          <th>Schema</th>
                          <th>Table</th>
                          <th>Privileges</th>
                        </>
                      ) : (
                        <>
                          <th>Name</th>
                          <th>Privileges</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {privilegeModalData.data.map((item, idx) => (
                      <tr key={idx}>
                        {privilegeModalData.type === 'roles' ? (
                          <>
                            <td>{item.granted_role || item.role_name || (typeof item === 'string' ? item : JSON.stringify(item))}</td>
                            <td>{item.admin_option ? 'Yes' : 'No'}</td>
                          </>
                        ) : privilegeModalData.type === 'table_privs' ? (
                          <>
                            <td>{item.schema_name || item.table_schema}</td>
                            <td>{item.table_name}</td>
                            <td>{Array.isArray(item.privileges) ? item.privileges.join(', ') : item.privilege_type}</td>
                          </>
                        ) : (
                          <>
                            <td>{item.schema_name || item.database_name || item.function_name || JSON.stringify(item)}</td>
                            <td>{Array.isArray(item.privileges) ? item.privileges.join(', ') : (item.privilege_type || '-')}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-muted">No {privilegeModalData.type.replace('_', ' ')} found</p>
            )}
          </>
        )}
      </WideModal>
    </div>
  );
};

export default PostgresqlUserAccessReport;
